import Hue from '@assets/hue_yellow.svg';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import { easeInOut } from '@config/animations.config';
import { IProductCard } from '@models/IProductCard';
import Tags from '@molecules/Tags/Tags';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props extends IProductCard {
  readonly addToBasketLabel?: string;
  readonly classes?: string;
  readonly dataTestId?: string;
  readonly productNameClasses?: string;
  readonly useShortCardOnMobile?: boolean;
  readonly onAddToCart?: (code: string) => void;
}

const ProductCard = ({
  addToBasketLabel,
  buyNowLink,
  category,
  classes,
  fromPriceLabel,
  hidePriceFromSavePill,
  image,
  learnMoreLink,
  name,
  newProductLabel,
  pricingData,
  productNameClasses,
  savePriceLabel,
  shippingLabel,
  sku,
  statusLabels,
  tabletImageContainerHeight,
  thumbnail,
  trackingData,
  useShortCardOnMobile = false,
  vatLabel,
  dataTestId,
  onAddToCart,
}: Props) => {
  const [isHovered, setHover] = useState(false);
  const isXLarge = useMediaQuery(1440);
  const [ctaBoxHeight, setCtaBoxHeight] = useState(0);
  const ctaBox = useRef<HTMLDivElement>(null);
  const trackingDataJson = JSON.stringify(trackingData ?? '');

  const handleIntentToBuyEvent = (contentType: string) => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'intentToBuy',
      content_type: contentType,
      items: [mapToNewTrackingData(trackingData)],
    });
  };

  const handleSelectItemEvent = () => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'selectItem',
      ecommerce: {
        currency: trackingData.currency,
        items: [mapToNewTrackingData(trackingData)],
      },
    });
  };

  useEffect(() => {
    setCtaBoxHeight(ctaBox.current?.clientHeight || 0);
  }, []);

  const contVariants = {
    hover: {
      y: -ctaBoxHeight,
    },
    initial: { y: 0 },
  };

  const ctaVariants = {
    hover: { opacity: 1 },
    initial: { opacity: isXLarge ? 1 : 0 },
  };

  const imgVariants = {
    hover: {
      y: -15,
      scale: 1.1,
    },
    initial: { y: 0, scale: 1 },
  };

  const hueVariants = {
    hover: { opacity: 0.2 },
    initial: { opacity: 0 },
  };

  return (
    <div
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      track-product-impression="true"
      data-product={trackingDataJson}
      className={classNames(
        'pointer-events-auto relative flex h-full w-full flex-col justify-end gap-5 overflow-hidden rounded-[10px] bg-grey-200 px-5 py-8 xl:px-7 xl:py-15 dark:bg-grey-600',
        classes
      )}
      data-testid={dataTestId}
    >
      {image && (
        <Image
          image={image}
          classes="z-5 absolute top-0 right-0 left-0 m-2.5 md:m-5"
          imageClasses="rounded-[10px] object-contain"
        />
      )}
      {(savePriceLabel || newProductLabel || statusLabels) && (
        <div className="absolute top-0 left-0 z-20 flex w-full flex-row flex-wrap gap-4 p-5">
          <Tags
            dataTestId="Product-Card-Tag"
            hidePriceFromSavePill={hidePriceFromSavePill}
            newProductLabel={newProductLabel}
            pricingData={pricingData}
            savePriceLabel={savePriceLabel}
            statusLabels={statusLabels}
          />
        </div>
      )}
      <div
        className={classNames(
          'relative flex h-full w-full flex-col lg:gap-4 2xl:gap-7.5',
          useShortCardOnMobile ? 'gap-4' : 'gap-8'
        )}
      >
        <motion.div
          initial="initial"
          className={classNames(
            'relative flex h-[254px] w-full shrink-0 items-center justify-center lg:h-[220px] xl:h-[250px] xl:pb-10 2xl:h-[300px]',
            tabletImageContainerHeight ?? 'md:h-[165px]'
          )}
          animate={isHovered && !isXLarge ? 'hover' : 'initial'}
          variants={imgVariants}
          transition={easeInOut}
        >
          {thumbnail && (
            <a
              href={learnMoreLink?.url || buyNowLink?.url}
              onClick={() => {
                if (learnMoreLink?.url)
                  handleIntentToBuyEvent('product picture');
                else if (buyNowLink?.url) handleSelectItemEvent();
              }}
              aria-label={name}
              className="flex h-full w-full"
            >
              <Image
                image={thumbnail}
                classes="z-10 w-full h-full"
                imageClasses="w-full h-full object-contain"
                sizes="(max-width: 768px) 376px, 500px"
                dataTestId="Product-Card-Image"
              />

              <motion.div
                initial="initial"
                animate={isHovered && !isXLarge ? 'hover' : 'initial'}
                variants={hueVariants}
                className="pointer-events-none absolute top-10 left-0 z-0 flex h-full scale-150 items-center justify-center"
              >
                <img
                  src={`${Hue}`}
                  alt="hue"
                  className="hidden w-full object-contain dark:block"
                />
              </motion.div>
            </a>
          )}
        </motion.div>

        <motion.div
          initial="initial"
          animate={isHovered && !isXLarge ? 'hover' : 'initial'}
          variants={contVariants}
          transition={easeInOut}
          className="relative flex w-full grow flex-col items-center justify-center gap-3 dark:text-white"
        >
          {category && (
            <p
              className="body-two text-center"
              data-testid="Product-Card-Category"
            >
              {category}
            </p>
          )}

          {name && (
            <p
              className={classNames(
                'h5 pb-7.5 text-center',
                productNameClasses
              )}
              data-testid="Product-Card-Name"
            >
              {name}
            </p>
          )}

          {pricingData?.formattedCalculatedPrice && (
            <PriceDisplayText
              pricingData={pricingData}
              vatLabel={vatLabel}
              shippingLabel={shippingLabel}
              fromPriceLabel={fromPriceLabel}
              centerText
              containerClassName="mt-auto"
              dataTestId="Product-Card-Price-Display-Text"
            />
          )}

          <motion.div
            initial="initial"
            animate={isHovered && !isXLarge ? 'hover' : 'initial'}
            variants={ctaVariants}
            transition={{
              duration: 0.3,
            }}
            ref={ctaBox}
            className={classNames(
              'flex flex-col flex-wrap items-center justify-center gap-5 xl:absolute xl:top-full xl:flex-row xl:gap-[10px]',
              useShortCardOnMobile ? 'pt-2' : 'pt-5'
            )}
            data-testid="Product-Card-Item-Hover"
          >
            {learnMoreLink?.name && learnMoreLink?.url && (
              <Button
                link={learnMoreLink}
                secondary
                linkCallback={() =>
                  handleIntentToBuyEvent(learnMoreLink.name || 'Learn more')
                }
                dataTestId="Product-Card-Learn More"
              />
            )}

            {buyNowLink?.url && buyNowLink?.name && (
              <a
                href={buyNowLink.url}
                track-product-click="true"
                data-product={trackingDataJson}
                className="cta primary"
                data-testid="Product-Card-Buy-Now"
                onClick={handleSelectItemEvent}
              >
                {buyNowLink.name}
              </a>
            )}

            {!(buyNowLink?.url && buyNowLink?.name) &&
              addToBasketLabel &&
              sku &&
              onAddToCart && (
                <Button
                  onClick={() => onAddToCart(sku)}
                  dataTestId="Product-Card-Add-To-Cart"
                >
                  {addToBasketLabel}
                </Button>
              )}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductCard;
